/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Type } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * Context route can exist under page loaded by @angular/router, dialog opened by {@link DialogService}
 * or embedded by {@link ContextRouterComponent}.
 * This enumeration defines the context of context route.
 */
export enum ContextRouteAttachment {
  Router,
  Dialog,
  Inline,
}

/**
 * Instance of this class is available for injection under any context routes.
 * It allows to access route parameters, loaded data, route configuration, etc.
 */
export class ContextRoute {
  constructor(public config: ContextRouteConfig,
              public attachment: ContextRouteAttachment,
              public params: Hash<any> = {},
              public data: Hash<any> = {}) {
  }
}

/**
 * Instance of this interface is assigned to the context route.
 * When user comes to the corresponding route, method {@link ContextResolve#resolve} is called.
 * Target route is opened only when observable or promise is resolved.
 * Loaded data is available under {@link ContextRoute#data} property.
 *
 * This functionality is similar to the corresponding @angular/router functionality.
 */
export interface ContextResolve<T> {
  resolve(route: ContextRoute): Observable<T> | Promise<T> | T;
}

export type ContextResolveFn = (route: ContextRoute) => Observable<any> | Promise<any> | any;

/**
 * Instance of this interface is assigned to the context route.
 * When user tries to leave corresponding route, method {@link ContextCanDeactivate#canDeactivate} is called
 * to determine whether it is allowed to leave this route or not.
 *
 * This functionality is similar to the corresponding @angular/router functionality.
 */
export interface ContextCanDeactivate<T> {
  /**
   * This method should return boolean flag synchronously or asynchornously.
   * False value allows to prevent routing to the next route.
   */
  canDeactivate(component: T, route: ContextRoute): Observable<boolean> | Promise<boolean> | boolean;
}

/**
 * Defines route config similar to @angular/router {@link Route} interface.
 */
export interface ContextRouteConfig {
  path: string;
  component: Type<any>;
  data: Hash<any>;
  resolve?: Hash<Type<ContextResolve<any>> | ContextResolveFn>;
  canDeactivate?: Type<ContextCanDeactivate<any>>;
}
